// @ts-check

/**
 * @typedef {import('./strategies/PaypalStrategy').PaypalStrategy} PaypalStrategy
 * @typedef {import('./strategies/PaypalStrategy').PaypalInfo} PaypalInfo
 */

/**
 * PayPal payment method implementation.
 * Follows the standard payment method pattern (Strategy Pattern).
 * @function PayPal
 */
const PayPal = () => {
  /**
   * @type {PaypalStrategy | undefined}
   */
  let strategy;

  /**
   * Sets the PayPal strategy.
   * @param {PaypalStrategy} newStrategy - The PayPal strategy to use.
   */
  const setStrategy = (newStrategy) => {
    strategy = newStrategy;
  };

  /**
   * Creates the payload for PayPal payment.
   * @param {PaypalInfo} paypalInfo - PayPal payment information
   * @returns {Promise<Object>} The created payload for Purchase API
   * @throws {Error} If strategy is not set or payload creation fails
   */
  const createPayload = async (paypalInfo) => {
    if (!strategy) {
      throw new Error('PayPal strategy not set');
    }

    return strategy.createPayload(paypalInfo);
  };

  /**
   * Checks if PayPal is eligible for the current user/session.
   * @param {Object} config - PayPal configuration
   * @param {string|null} clientToken - PayPal client token (required only if SDK is not initialized)
   * @returns {Promise<{methods: Object}>} Eligibility result with details for all payment methods
   * @throws {Error} If strategy is not set
   */
  const checkEligibility = async (config, clientToken = null) => {
    if (!strategy) {
      throw new Error('PayPal strategy not set');
    }

    return strategy.checkEligibility(config, clientToken);
  };

  /**
   * Processes a PayPal payment by initializing the SDK and starting the payment flow.
   * @param {Object} config - PayPal configuration
   * @param {Object} paymentData - Payment data
   * @param {Object} callbacks - PayPal session callbacks
   * @param {string|null} clientToken - PayPal client token (required only if SDK is not initialized)
   * @returns {Promise<Object>} Payment session object
   * @throws {Error} If strategy is not set or payment processing fails
   */
  const processPayment = async (config, paymentData, callbacks, clientToken = null) => {
    if (!strategy) {
      throw new Error('PayPal strategy not set');
    }

    return strategy.processPayment(config, paymentData, callbacks, clientToken);
  };

  return {
    setStrategy,
    createPayload,
    checkEligibility,
    processPayment,
  };
};

// Create singleton instance
const paypalMethod = PayPal();

export default paypalMethod;
